import type { OfficePresetManifest } from "@bezoned/shared/schemas/office/office-preset-manifest.schema.js";
import { UserAvailabilityOptions } from "@bezoned/shared/types/user-presence.js";
import { Video20Regular } from "@fluentui/react-icons";
import { Html } from "@react-three/drei";
import type { ThreeEvent } from "@react-three/fiber";
import { useCallback, useEffect, useState } from "react";
import { Mesh, Vector3 } from "three";
import type { Object3D } from "three";

import { GeneralLabel } from "../../components/common/general-label.js";
import { GeneralOfficeAction } from "../../components/ui/actions/general-office-action.js";
import { SeatedAvatarList } from "../../components/ui/office/avatars/seated-avatar-list.js";
import { UnseatedAvatarList } from "../../components/ui/office/avatars/unseated-avatar-list.js";
import { HoverBox } from "../../components/ui/office/hover-box.js";
import { useAvatarSeating } from "../../hooks/use-avatar-seating.js";
import { useBezonedContext } from "../../hooks/use-bezoned-context.js";
import { useCenteredGltf } from "../../hooks/use-centered-gltf.js";
import { useRoomInteraction } from "../../hooks/use-room-interaction.js";
import { useTeamsNavigation } from "../../hooks/use-teams-navigation.js";
import { joinMeeting } from "../../services/api.service.js";
import { recordErrorInSentry } from "../../utils/init-sentry.js";
import { getRoom, isUserInRoom } from "../fluid/fluid.service.js";
import { useFluid } from "../fluid/hooks/use-fluid.js";
import { useMyPresence } from "../presence/use-my-presence.js";

type GeneralMeetingProps = OfficePresetManifest["meetingRooms"][number] & {
  meetingRoomName: string;
  meetingRoomId: string;
  modelPath: string;
};

const CENTER_ELEVATION_OFFSET = 2.5;
const MAX_JOIN_MEETING_ATTEMPTS = 10;
const JOIN_MEETING_RETRY_DELAY_MS = 2000;

const waitForMeetingRetry = () =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, JOIN_MEETING_RETRY_DELAY_MS);
  });

const joinMeetingWithRetry = async (
  meetingRoomId: string,
  navigateToRoomMeeting: (url: string) => Promise<void>
) => {
  for (let attempt = 1; attempt <= MAX_JOIN_MEETING_ATTEMPTS; attempt++) {
    const result = await joinMeeting({ meetingRoomId });

    if (!result.checkAgain && result.url) {
      await navigateToRoomMeeting(result.url);
      return;
    }

    if (result.checkAgain && attempt < MAX_JOIN_MEETING_ATTEMPTS) {
      await waitForMeetingRetry();
      continue;
    }

    return;
  }
};

export const GeneralMeeting = ({
  modelPath,
  meetingRoomId,
  meetingRoomName,
  rotation,
  scale,
  position,
  hoverBox,
  text,
  seating,
}: GeneralMeetingProps) => {
  const { ContextBridge } = useBezonedContext();
  const { navigateToRoomMeeting } = useTeamsNavigation();
  const { fluidUserId, sharedTree } = useFluid();
  const { groupRef, hovered, handleRoomClick } = useRoomInteraction({
    roomId: meetingRoomId,
  });
  const { updateAvailability } = useMyPresence();
  const { center, modelBottomY, modelHeight, clonedGltf } =
    useCenteredGltf(modelPath);
  const [isLoading, setIsLoading] = useState(false);

  // Get the room node for this location (Fluid node reference is stable)
  const room = getRoom({ roomId: meetingRoomId, sharedTree });

  // Use the shared hook for user filtering and seat allocation
  // Pass the room node so the hook can subscribe to changes on this specific room
  const { seatedUsers, overflowUsers } = useAvatarSeating({
    getParticipantIds: () => [...(room?.users ?? [])],
    locationNode: room,
    seatCount: seating?.seats?.length ?? 0,
  });

  useEffect(() => {
    const clonedModel = clonedGltf();
    clonedModel.traverse((child: Object3D) => {
      if (child instanceof Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
    // Position model at local origin since group handles world position/rotation
    clonedModel.rotation.set(0, 0, 0);
    clonedModel.position.set(-center.x, -center.y, -center.z);
    clonedModel.scale.set(...scale);
    groupRef.current?.add(clonedModel);

    return () => {
      groupRef.current?.remove(clonedModel);
    };
  }, [center, clonedGltf, groupRef, scale]);

  const handleJoinMeeting = useCallback(async () => {
    if (
      !isUserInRoom({ roomId: meetingRoomId, sharedTree, userId: fluidUserId })
    ) {
      return;
    }

    setIsLoading(true);

    try {
      await joinMeetingWithRetry(meetingRoomId, navigateToRoomMeeting);
    } catch (error) {
      recordErrorInSentry(error);
    } finally {
      setIsLoading(false);
    }
  }, [fluidUserId, meetingRoomId, sharedTree, navigateToRoomMeeting]);

  const moveToMeetingRoomAndSetBusy = useCallback(
    async (event: ThreeEvent<MouseEvent>) => {
      if (!handleRoomClick(event)) {
        return;
      }

      try {
        await updateAvailability(UserAvailabilityOptions.Busy);
      } catch (error) {
        recordErrorInSentry(error);
      }
    },
    [handleRoomClick, updateAvailability]
  );

  return (
    // Lint exception: react-three-fiber groups handle 3D pointer interactions.
    <group
      onClick={moveToMeetingRoomAndSetBusy}
      position={position as [number, number, number]}
      ref={groupRef}
      rotation={rotation as [number, number, number]}
      userData={{ roomId: meetingRoomId }}
    >
      <HoverBox
        config={hoverBox}
        hovered={hovered}
        modelBottomY={modelBottomY}
        modelHeight={modelHeight}
        onClick={moveToMeetingRoomAndSetBusy}
      />

      <GeneralLabel config={text} hovered={hovered} value={meetingRoomName} />

      <SeatedAvatarList
        seats={
          seating?.seats?.map(
            (seat) => new Vector3(seat[0], seat[1], seat[2])
          ) ?? []
        }
        users={seatedUsers}
      />

      <UnseatedAvatarList
        offset={
          new Vector3(
            ...(seating?.listOffset ?? seating?.seats?.at(-1) ?? [0, 0, 0])
          )
        }
        users={overflowUsers}
      />

      <Html
        position={new Vector3(0, CENTER_ELEVATION_OFFSET, 0)}
        sprite
        style={{
          pointerEvents: "auto",
          userSelect: "none",
        }}
        transform
      >
        <div
          style={{
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
            justifyContent: "center",
            pointerEvents: "auto",
          }}
        >
          <ContextBridge>
            {isUserInRoom({
              roomId: meetingRoomId,
              sharedTree,
              userId: fluidUserId,
            }) && (
              <GeneralOfficeAction
                icon={<Video20Regular />}
                label="Join Meeting"
                loading={isLoading}
                onClick={handleJoinMeeting}
                variant="secondary"
              />
            )}
          </ContextBridge>
        </div>
      </Html>
    </group>
  );
};
